import { useState } from "react";
import { toast } from "sonner";
import { Search, Trash2 } from "lucide-react";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg, fmtDate } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { AdminH } from "./AdminLayout";
import { ParticipantSheet } from "./ParticipantSheet";

export const deleteUser = async (u, done) => {
  if (!window.confirm(`Delete ${u.first_name} ${u.last_name} (${u.email})?\nTheir sailors, registrations, payments, receipts and documents will be deleted permanently.`)) return;
  try {
    await api.delete(`/admin/users/${u.id}`);
    toast.success("User deleted");
    done();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

export default function AdminUsers() {
  const [q, setQ] = useState("");
  const { data, reload } = useFetch(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  const { data: companies } = useFetch("/admin/companies");
  const [open, setOpen] = useState(null);
  const setRole = async (u, role, company_id) => {
    if (role === "collaborator") {
      const cid = company_id || u.collab_company_id || companies?.find((c) => c.is_default)?.id || companies?.[0]?.id;
      if (!cid) return toast.error("Add an organizing company first (Site settings › Companies)");
      const name = companies.find((c) => c.id === cid)?.name;
      if (!window.confirm(`Make ${u.first_name} ${u.last_name} collaborator of ${name} (only Proforma and Invoices of that company)?`)) return;
      try { await api.patch(`/admin/users/${u.id}/role`, { role, company_id: cid }); toast.success("User type updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
      return;
    }
    if (!window.confirm(role === "admin" ? `Give ${u.first_name} ${u.last_name} full administrator access?` : role === "staff" ? `Make ${u.first_name} ${u.last_name} staff (instructor: can view registrants, not payments or settings)?` : `Change ${u.first_name} ${u.last_name} to a standard user?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role });
      toast.success("User type updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
    reload();
  };
  return (
    <div data-testid="admin-users">
      <AdminH title="Users" sub="Registered accounts and their sailors. User type: admin can do everything; staff (instructors) can view registrants but not payments or settings; user is the standard account created at registration. Only admins can change user types. Deleting a user also removes their registrations and payments." />
      <div className="relative mb-4 max-w-md"><Search size={16} className="absolute left-3 top-3 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="h-10 rounded-xl pl-9" data-testid="users-search" /></div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Account holder</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">User type</th><th className="px-4 py-3">Sailors</th><th className="px-4 py-3">Registered</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data || []).map((u) => (
              <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3"><button onClick={() => setOpen(u.id)} className="font-semibold text-[#0A2540] hover:underline" data-testid={`user-open-${u.id}`}>{u.first_name} {u.last_name}</button></td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">
                  <select value={u.role} onChange={(e) => setRole(u, e.target.value)} className={`h-8 rounded-lg border px-2 text-xs font-semibold ${u.role === "admin" ? "border-[#FF5722] text-[#FF5722]" : "border-slate-200 text-slate-600"}`} data-testid={`user-role-${u.id}`}>
                    <option value="user">user</option><option value="staff">staff</option><option value="collaborator">collaborator</option><option value="admin">admin</option>
                  </select>
                  {u.role === "collaborator" && <select value={u.collab_company_id || ""} onChange={(e) => setRole(u, "collaborator", e.target.value)} className="ml-2 h-8 rounded-lg border border-[#00A8B5] px-2 text-xs" data-testid={`user-collab-company-${u.id}`}>
                    {(companies || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
                </td>
                <td className="px-4 py-3">{u.participants.map((p) => `${p.first_name}${p.is_minor ? " (minor)" : ""}`).join(", ")}</td>
                <td className="px-4 py-3">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right">{u.role !== "admin" && <button onClick={() => deleteUser(u, reload)} className="text-slate-400 hover:text-rose-600" title="Delete user" data-testid={`user-delete-${u.id}`}><Trash2 size={16} /></button>}</td>
              </tr>
            ))}
            {data && !data.length && <tr><td className="px-4 py-6 text-slate-500" colSpan={6}>No users.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <ParticipantSheet userId={open} onClose={() => { setOpen(null); reload(); }} />}
    </div>
  );
}
